/**
 * Canonical mapping from a model-emitted `document_title` to the source it
 * refers to. Shared so the server and the citation UI cannot drift apart on
 * what counts as a transcript, a lab, or a note.
 */

export const CITATION_SOURCE_SLUGS = [
  'labResults',
  'medications',
  'intakeForm',
  'transcript',
  'patientDetails',
  'clinicalNote',
  'conditions',
  'patientOverview',
  'previousRx',
  'allergies',
  'source',
] as const;

export type CitationSourceSlug = (typeof CITATION_SOURCE_SLUGS)[number];

export interface CitationSource {
  slug: CitationSourceSlug;
  label: string;
}

// First match wins, so keep the more specific keywords ahead of the ones they
// contain.
const KEYWORD_SOURCES: ReadonlyArray<readonly [string, CitationSource]> = [
  ['lab', { slug: 'labResults', label: 'Lab Results' }],
  ['medication', { slug: 'medications', label: 'Medications' }],
  ['intake', { slug: 'intakeForm', label: 'Intake Form' }],
  ['transcript', { slug: 'transcript', label: 'Transcript' }],
  ['patient detail', { slug: 'patientDetails', label: 'Patient Details' }],
  ['medical note', { slug: 'clinicalNote', label: 'Clinical Note' }],
  ['condition', { slug: 'conditions', label: 'Conditions' }],
  ['overview', { slug: 'patientOverview', label: 'Patient Overview' }],
  ['previous rx', { slug: 'previousRx', label: 'Previous Rx' }],
  ['allergy', { slug: 'allergies', label: 'Allergies' }],
  ['clinical note', { slug: 'clinicalNote', label: 'Clinical Note' }],
  ['temp', { slug: 'source', label: 'Source' }],
  // Catch-all for note titles the specific keywords above miss, e.g. the DDX
  // agent's "Recent Signed Notes". Last, so `temp*` still resolves to Source.
  ['note', { slug: 'clinicalNote', label: 'Clinical Note' }],
];

/**
 * Returns null when no keyword matches, so each caller applies its own
 * fallback presentation rather than sharing one it did not choose.
 */
export const resolveCitationSource = (
  documentTitle: string | undefined,
): CitationSource | null => {
  const title = (documentTitle ?? '').toLowerCase();
  return (
    KEYWORD_SOURCES.find(([keyword]) => title.includes(keyword))?.[1] ?? null
  );
};
